import React, { useMemo, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Spinner } from "react-bootstrap";
import { colors } from "app/popleads/components/ui-kit/colors";
import SelectableTaskCard from "app/popleads/components/ui-kit/SelectableTaskCard";
import DateHeader from "app/popleads/components/ui-kit/DateHeader";
import moment from "moment";
import { StyledFooterButton } from "./ExtensionNewTask";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import Switch from "app/popleads/components/ui-kit/Switch";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "_metronic/_helpers";
import useCompletedTasks from "app/popleads/hooks/useGetCompletedTasks";
import useTaskDelete from "app/popleads/hooks/useTaskDelete";
import useTaskComplete from "app/popleads/hooks/useTaskComplete";
import useUpdateTasksTime from "app/popleads/hooks/useUpdateTasksTime";
import { useExtensionContext } from "../ExtensionContext";
import { ExtensionCardHeader } from "app/popleads/components/ui-kit/ExtensionCard";
import { POPUP_CONTENTS, tagOptions } from "../utils/constants";
import { toast } from "react-toastify";
import CustomDatePicker from "app/popleads/components/ui-kit/DateInput";
import Modal from "app/popleads/components/ui-kit/Modal";
import { post } from "app/popleads/networking/RequestService";
import ExtensionInputGroup from "./ExtensionInputGroup";

const TaskListContainer = styled.div`
    height: calc(100% - 107px);
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: 100%;
    box-sizing: border-box;
`;

const DateHeaderContainer = styled.div`
    position: sticky;
    top: 0;
    background-color: ${colors.neutral[50]};
    z-index: 1111;
    padding: 12px 0;
    padding: 0 12px;
    overflow: visible;
`;

const SelectAllContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 12px;
    background-color: ${colors.neutral[50]};
    border-bottom: 1px solid ${colors.neutral[200]};
`;

const SelectAllCheckbox = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid ${(props) => (props.checked ? colors.l3rnTech[900] : colors.neutral[500])};
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background-color: ${(props) => (props.checked ? colors.l3rnTech[900] : "transparent")};
    transition: all 0.2s ease;
    margin: 0;

    &:after {
        content: ${(props) => (props.checked ? "'✓'" : "''")};
        color: ${colors.white};
        font-size: 12px;
        font-weight: bold;
        line-height: 1;
    }
`;

const SelectAllLabel = styled.label`
    color: ${colors.neutral[900]};
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    user-select: none;
    margin: 0;
    line-height: 16px;
`;

const BulkActionsContainer = styled.div`
    display: flex;
    justify-content: space-around;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 0 12px;
    box-sizing: border-box;
    overflow: hidden;
`;

const TasksContainer = styled.div`
    flex: 1;
    overflow-y: visible;
    overflow-x: hidden;
    padding: 0 12px;
    padding-bottom: 10px;
`;

const StyledButtonContainer = styled(StyledFooterButton)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: absolute;
    width: inherit;
`;

const BulkActionsFooter = styled.div`
    position: absolute;
    bottom: 58px;
    left: 0;
    right: 0;
    background-color: ${colors.white};
    border-top: 1px solid ${colors.neutral[200]};
    z-index: 5;
    overflow: hidden;
    width: 100%;
    box-sizing: border-box;
`;

const FilterBar = styled.div`
    background-color: ${colors.white};
    border-bottom: 1px solid ${colors.neutral[200]};
    padding: 5px;

    .filter-dropdown-container {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
    }

    .filter-label {
        color: ${colors.neutral[900]};
        font-size: 14px;
        font-weight: 500;
        min-width: fit-content;
    }
`;

const DashedDivider = styled.hr`
    border: none;
    border-top: 1px dashed ${colors.neutral[200]};
    margin: 16px 0 8px 0;
`;

const TimeDisplay = styled.div`
    color: ${colors.neutral[700]};
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 8px;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    padding: 20px;
`;

const NoTasksMessage = styled.div`
    text-align: center;
    padding: 20px;
    color: ${colors.neutral[500]};
`;

const StyledText = styled.div`
    color: ${colors.neutral[900]};
    font-size: 12px;
`;
const StyledFlex = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;
const StyledTaskContainer = styled.div`
    margin-bottom: 12px;
    overflow: visible;

    &:last-child {
        margin-bottom: 20px;
    }
`;

const TimeGroup = styled.div`
    margin-bottom: 16px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const HeaderTitle = styled.div`
    color: ${colors.neutral[900]};
    font-size: 18px;
    font-weight: 500;
`;

const MoveButtonWrapper = styled.div`
    position: relative;
    padding: 4px 0;
    overflow: visible;
    z-index: 1;
    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: -20px;
        right: -20px;
        height: 100%;
        pointer-events: none;
    }

    &:hover .move-dropdown {
        display: ${(props) => (props.showDatePicker ? "none" : "block")};
    }

    .move-dropdown:hover {
        display: ${(props) => (props.showDatePicker ? "none" : "block")};
    }

    .move-dropdown {
        display: ${(props) => (props.showDatePicker ? "none !important" : "none")};
    }
`;

const MoveDropdown = styled.div`
    position: fixed;
    background: ${colors.white};
    border: 1px solid ${colors.neutral[100]};
    border-radius: 6px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    padding: 8px 0;
    display: none;
    min-width: 100px;
    max-width: 120px;
    z-index: 999999;
    max-height: 160px;
    transform: translate(-18px, -194px);

    &:after {
        content: "";
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 8px solid ${colors.white};
        z-index: 2;
    }

    &:before {
        content: "";
        position: absolute;
        bottom: -9px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 9px solid transparent;
        border-right: 9px solid transparent;
        border-top: 9px solid ${colors.neutral[100]};
        z-index: 1;
    }

    &.dropdown-above {
        &:after {
            content: "";
            position: absolute;
            bottom: -8px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-bottom: 8px solid ${colors.white};
            z-index: 2;
        }

        &:before {
            content: "";
            position: absolute;
            bottom: -9px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 9px solid transparent;
            border-right: 9px solid transparent;
            border-bottom: 9px solid ${colors.neutral[100]};
            z-index: 1;
        }
    }

    &.dropdown-below {
        &:after {
            content: "";
            position: absolute;
            top: -8px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: 8px solid ${colors.white};
            z-index: 2;
        }

        &:before {
            content: "";
            position: absolute;
            top: -9px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 9px solid transparent;
            border-right: 9px solid transparent;
            border-top: 9px solid ${colors.neutral[100]};
            z-index: 1;
        }
    }
`;

const MoveOption = styled.div`
    padding: 8px 16px;
    color: ${colors.neutral[800]};
    font-size: 14px;
    cursor: pointer;

    &:hover {
        background: ${colors.neutral[50]};
    }
`;

const DatePickerWrapper = styled.div`
    position: fixed;
    z-index: 999999;
    background: ${colors.white};
    border-radius: 8px;
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
    border: 1px solid ${colors.neutral[200]};
    max-height: 300px;
    max-width: 280px;
    overflow: hidden;
`;

const TaskTypeFilterWrapper = styled.div`
    width: 100%;

    .extension-input-group {
        margin-bottom: 0;

        .select__control {
            min-height: 32px;
            border: 1px solid ${colors.neutral[300]};
            border-radius: 6px;
            font-size: 12px;

            &:hover {
                border-color: ${colors.neutral[400]};
            }

            &--is-focused {
                border-color: ${colors.primary[900]};
                box-shadow: 0 0 0 1px ${colors.primary[900]};
            }
        }

        .select__value-container {
            padding: 2px 8px;
        }

        .select__placeholder {
            color: ${colors.neutral[500]};
            font-size: 12px;
        }

        .select__multi-value {
            font-size: 11px;

            &__label {
                padding: 2px 4px;
            }

            &__remove {
                padding: 0 4px;
            }
        }

        .select__menu {
            font-size: 12px;
        }
    }
`;

const ExtensionTaskList = ({ taskList, taskListLoading, setTaskToDelete }) => {
    const { getCompletedTasks } = useCompletedTasks();
    const { bulkDeleteTasks, loading: deleteLoading } = useTaskDelete();
    const { bulkCompleteTasks, loading: completeLoading } = useTaskComplete();
    const { updateTasksTime, loading: moveLoading } = useUpdateTasksTime();
    const {
        selectedDate,
        setState,
        liveTasks,
        setLiveTasks,
        selectedTasksId,
        staticTaskList,
        fetchStaticTaskList,
        staticTaskListLoading,
        modals,
        handleBulkDelete: contextBulkDelete,
        selectedModal,
        modalMessage,
        showOverdueTasks,
    } = useExtensionContext();
    const [tasks, setTasks] = useState([]);
    const [selectedTasks, setSelectedTasks] = useState([]);
    const [isActive, setIsActive] = useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedTaskTypes, setSelectedTaskTypes] = useState([]);
    const [showFilter, setShowFilter] = useState(false);
    const taskRefs = useRef({});
    const tasksContainerRef = useRef(null);
    const moveButtonRef = useRef(null);
    const moveDropdownRef = useRef(null);
    const datePickerRef = useRef(null);

    const [overdueTasks, setOverdueTasks] = useState([]);
    const [isOverdueTasksLoading, setIsOverdueTasksLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMoreOld, setHasMoreOld] = useState(true);
    const loaderRef = useRef(null);

    const selectedDayStart = moment(selectedDate)
        .startOf("day")
        .valueOf();
    const selectedDayEnd = moment(selectedDate)
        .endOf("day")
        .valueOf();

    useEffect(() => {
        fetchStaticTaskList();
    }, [fetchStaticTaskList]);

    useEffect(() => {
        setLiveTasks([]);
    }, [selectedDate]);

    useEffect(() => {
        setSelectedTaskTypes([]);
        setShowFilter(false);
    }, [selectedDate, showOverdueTasks, isActive]);

    useEffect(() => {
        if (selectedTasksId && taskRefs.current[selectedTasksId] && tasksContainerRef.current) {
            setTimeout(() => {
                const taskElement = taskRefs.current[selectedTasksId];
                const container = tasksContainerRef.current;

                if (!taskElement || !container) return;

                try {
                    const containerRect = container.getBoundingClientRect();
                    const taskRect = taskElement.getBoundingClientRect();

                    if (containerRect.height === 0 || taskRect.height === 0) return;

                    const scrollTop =
                        container.scrollTop +
                        taskRect.top -
                        containerRect.top -
                        containerRect.height / 2 +
                        taskRect.height / 2;

                    container.scrollTo({
                        top: Math.max(0, scrollTop),
                        behavior: "smooth",
                    });
                } catch (error) {
                    toast.error("Scroll to task failed");
                }
            }, 100);
        }
    }, [selectedTasksId, tasks]);

    useEffect(() => {
        const handleTasks = async () => {
            if (isActive) {
                const completedTasksData = await getCompletedTasks({ selectedDate });
                if (completedTasksData?.completedTasks?.length > 0) {
                    const sortedTasks = sortTasksByTime(completedTasksData.completedTasks);
                    setTasks(sortedTasks);
                    setSelectedTasks([]);
                } else {
                    setTasks([]);
                    setSelectedTasks([]);
                }
            } else if (showOverdueTasks && overdueTasks?.length > 0) {
                setTasks(overdueTasks);
                setSelectedTasks([]);
            } else if (taskList?.gorev?.length > 0 && !showOverdueTasks) {
                const sortedTasks = sortTasksByTime(taskList.gorev);
                setTasks(sortedTasks);
                setSelectedTasks([]);
            } else {
                setTasks([]);
                setSelectedTasks([]);
            }
        };

        handleTasks();
    }, [isActive, selectedDate, taskList?.gorev, overdueTasks, showOverdueTasks, taskListLoading]);

    useEffect(() => {
        if (liveTasks.length > 0) {
            const filteredLiveTasks = liveTasks.filter(
                (task) => task.zaman >= selectedDayStart && task.zaman <= selectedDayEnd
            );
            if (filteredLiveTasks.length > 0) {
                const findTask = taskList?.gorev?.some((task) => task.id === filteredLiveTasks[0].id);
                if (!findTask) {
                    const newTasks = [...tasks, ...filteredLiveTasks];
                    const sortedTasks = sortTasksByTime(newTasks);
                    setTasks(sortedTasks);
                }
            }
            setLiveTasks([]);
        }
    }, [tasks, liveTasks]);

    const handleTaskCompletion = (taskId) => {
        if (taskId === null) {
            setSelectedTasks((prev) => prev.filter((id) => id !== taskId));
            return;
        }

        setSelectedTasks((prev) => {
            if (prev.includes(taskId)) {
                return prev.filter((id) => id !== taskId);
            } else {
                return [...prev, taskId];
            }
        });
    };

    const sortTasksByTime = (tasksToSort, fullDateCompare = false) => {
        return [...tasksToSort].sort((a, b) => {
            if (fullDateCompare) {
                return moment(b.zaman).valueOf() - moment(a.zaman).valueOf();
            } else {
                const timeA = moment(a.zaman).format("HH:mm");
                const timeB = moment(b.zaman).format("HH:mm");

                const [hoursA, minutesA] = timeA.split(":").map(Number);
                const [hoursB, minutesB] = timeB.split(":").map(Number);

                const timeAMinutes = hoursA * 60 + minutesA;
                const timeBMinutes = hoursB * 60 + minutesB;

                return timeAMinutes - timeBMinutes;
            }
        });
    };

    const groupTasksByTime = (tasks) => {
        if (showOverdueTasks) {
            return { "": tasks };
        }

        const groups = {};
        tasks.forEach((task) => {
            const timeKey = moment(task.zaman).format("hh:mm A");

            if (!groups[timeKey]) {
                groups[timeKey] = [];
            }
            groups[timeKey].push(task);
        });
        return groups;
    };

    /*const groupTasksByDate = (tasks) => {
        const groups = {};
        tasks.forEach((task) => {
            const dateKey = moment(task.zaman).format("MMM DD, YYYY");
            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(task);
        });
        return groups;
    };*/

    const fetchOverdueTasks = async (page) => {
        if (isOverdueTasksLoading) return;
        setIsOverdueTasksLoading(true);
        try {
            const res = await post({
                fn: "gorevListe",
                tarih: moment().valueOf(),
                old: 1,
                start: page === 0 ? 0 : -1,
                startOld: page * 50,
            });
            if (res?.data?.overdueTasks?.length > 0) {
                const sortedTasks = sortTasksByTime(res?.data?.overdueTasks, true);
                setOverdueTasks((prev) => (page === 0 ? sortedTasks : [...prev, ...sortedTasks]));
                setHasMoreOld(res?.data?.hasMoreOld > 0);
                setPage(page + 1);
            }
        } catch (error) {
            toast.error("Failed to fetch overdue tasks");
        } finally {
            setIsOverdueTasksLoading(false);
        }
    };

    useEffect(() => {
        if (showOverdueTasks && staticTaskListLoading) {
            fetchOverdueTasks(0);
        }
    }, [showOverdueTasks, staticTaskListLoading]);

    useEffect(() => {
        if (!showOverdueTasks || !hasMoreOld || isOverdueTasksLoading || page === 0) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                fetchOverdueTasks(page);
            }
        });

        if (loaderRef.current) {
            observer.observe(loaderRef.current);
        }

        return () => {
            if (loaderRef.current) observer.unobserve(loaderRef.current);
        };
    }, [hasMoreOld, isOverdueTasksLoading, page, taskListLoading]);

    // Handle dropdown positioning
    useEffect(() => {
        const handleDropdownPosition = () => {
            if (moveButtonRef.current && moveDropdownRef.current) {
                const buttonRect = moveButtonRef.current.getBoundingClientRect();
                const dropdown = moveDropdownRef.current;

                // Calculate dropdown height (approximate)
                const dropdownHeight = 120; // max-height from CSS
                const spaceAbove = buttonRect.top;

                // Position dropdown above the button
                dropdown.style.left = `${buttonRect.left + buttonRect.width / 2}px`;

                // Remove existing classes
                dropdown.classList.remove("dropdown-above", "dropdown-below");

                if (spaceAbove >= dropdownHeight) {
                    // Enough space above, position above button
                    dropdown.style.top = `${buttonRect.top - dropdownHeight - 8}px`;
                    dropdown.style.bottom = "auto";
                    dropdown.classList.add("dropdown-above");
                } else {
                    // Not enough space above, position below button
                    dropdown.style.top = `${buttonRect.bottom + 8}px`;
                    dropdown.style.bottom = "auto";
                    dropdown.classList.add("dropdown-below");
                }

                dropdown.style.transform = "translateX(-50%)";
            }
        };

        // Add event listeners for hover
        const moveButton = moveButtonRef.current;
        if (moveButton) {
            moveButton.addEventListener("mouseenter", handleDropdownPosition);
            return () => {
                moveButton.removeEventListener("mouseenter", handleDropdownPosition);
            };
        }
    }, []);

    // Handle window resize to reposition dropdown
    useEffect(() => {
        const handleResize = () => {
            if (moveDropdownRef.current && moveDropdownRef.current.style.display === "block") {
                const moveButton = moveButtonRef.current;
                if (moveButton) {
                    const buttonRect = moveButton.getBoundingClientRect();
                    const dropdown = moveDropdownRef.current;

                    // Calculate dropdown height (approximate)
                    const dropdownHeight = 120; // max-height from CSS
                    const spaceAbove = buttonRect.top;

                    dropdown.style.left = `${buttonRect.left + buttonRect.width / 2}px`;

                    // Remove existing classes
                    dropdown.classList.remove("dropdown-above", "dropdown-below");

                    if (spaceAbove >= dropdownHeight) {
                        // Enough space above, position above button
                        dropdown.style.top = `${buttonRect.top - dropdownHeight - 8}px`;
                        dropdown.style.bottom = "auto";
                        dropdown.classList.add("dropdown-above");
                    } else {
                        // Not enough space above, position below button
                        dropdown.style.top = `${buttonRect.bottom + 8}px`;
                        dropdown.style.bottom = "auto";
                        dropdown.classList.add("dropdown-below");
                    }
                }
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Handle date picker positioning
    useEffect(() => {
        if (showDatePicker && moveButtonRef.current && datePickerRef.current) {
            const buttonRect = moveButtonRef.current.getBoundingClientRect();
            const datePicker = datePickerRef.current;

            // Position date picker above the button
            datePicker.style.left = `${buttonRect.left + buttonRect.width / 2}px`;
            datePicker.style.top = `${buttonRect.top - 5}px`;
            datePicker.style.transform = "translateX(-50%) translateY(-100%)";
        }
    }, [showDatePicker]);

    const handleShowOverdueTasks = () => {
        setState((prevState) => ({
            ...prevState,
            showOverdueTasks: true,
        }));
        setIsActive(false);
        fetchOverdueTasks(0);
    };

    const handleShowTodayTasks = () => {
        setState((prevState) => ({
            ...prevState,
            showOverdueTasks: false,
            selectedDate: new Date(),
        }));
        setIsActive(false);
    };

    const highlightedDates = useMemo(() => {
        if (!staticTaskList?.gorev && !staticTaskList?.overdueTasks) return [];

        const dates = [];

        if (staticTaskList?.gorev) {
            staticTaskList.gorev.forEach((task) => {
                const date = moment(task?.zaman).format("YYYY-MM-DD");
                if (!dates.includes(date)) {
                    dates.push(date);
                }
            });
        }

        if (staticTaskList?.overdueTasks) {
            staticTaskList.overdueTasks.forEach((task) => {
                const date = moment(task?.zaman).format("YYYY-MM-DD");
                if (!dates.includes(date)) {
                    dates.push(date);
                }
            });
        }

        return dates;
    }, [staticTaskList?.gorev, staticTaskList?.overdueTasks]);

    const handleSelectAll = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);

        if (newSelectAll) {
            // Select all visible/filtered tasks
            const allVisibleTaskIds = filteredTasks.map((task) => task.id);
            setSelectedTasks(allVisibleTaskIds);
        } else {
            setSelectedTasks([]);
        }
    };

    useEffect(() => {
        const visibleTasks = filteredTasks.length > 0 ? filteredTasks : tasks;

        if (visibleTasks.length === 0) {
            setSelectAll(false);
        } else if (
            selectedTasks.length === visibleTasks.length &&
            selectedTasks.every((id) => visibleTasks.some((task) => task.id === id))
        ) {
            setSelectAll(true);
        } else {
            setSelectAll(false);
        }
    }, [selectedTasks, tasks, filteredTasks]);

    const handleDelete = () => {
        if (selectedTasks.length === 0) {
            toast.warning("Please select tasks to delete");
            return;
        }

        setState({
            selectedModal: modals.DELETE_BULK_TASKS,
            modalMessage: "Are you sure want to delete all selected task?",
        });
    };

    const handleBulkDelete = async () => {
        const onSuccess = () => {
            const remainingTasks = tasks.filter((task) => !selectedTasks.includes(task.id));
            setTasks(remainingTasks);
            setSelectedTasks([]);
            setSelectAll(false);
        };

        await contextBulkDelete(selectedTasks, bulkDeleteTasks, onSuccess);
    };

    const handleComplete = async () => {
        if (selectedTasks.length === 0) {
            toast.warning("Please select tasks to complete");
            return;
        }

        try {
            await bulkCompleteTasks(selectedTasks);

            const remainingTasks = tasks.filter((task) => !selectedTasks.includes(task.id));
            setTasks(remainingTasks);
            setSelectedTasks([]);
            setSelectAll(false);

            toast.success(`${selectedTasks.length} task(s) completed successfully`);

            fetchStaticTaskList();
        } catch (error) {
            toast.error("Failed to complete tasks");
        }
    };

    const handleMove = async (days) => {
        if (selectedTasks.length === 0) {
            toast.warning("Please select tasks to move");
            return;
        }

        try {
            if (days === "other") {
                setShowDatePicker(true);
                return;
            }

            // For overdue tasks, calculate new date based on today + period
            // For regular tasks, use period-based movement

            // Check if we're dealing with overdue tasks
            const firstTask = tasks.find((task) => selectedTasks.includes(task.id));
            const isOverdue = firstTask && moment(firstTask.zaman).isBefore(moment(), "day");

            if (showOverdueTasks || isOverdue) {
                // For overdue tasks: today + days, preserving original time
                if (firstTask) {
                    const originalTime = moment(firstTask.zaman);
                    const today = moment().startOf("day");
                    const newDate = today
                        .add(days, "days")
                        .hour(originalTime.hour())
                        .minute(originalTime.minute())
                        .second(originalTime.second())
                        .valueOf();
                    await updateTasksTime(selectedTasks, { zaman: newDate });
                }
            } else {
                // For future tasks: use period-based movement
                await updateTasksTime(selectedTasks, { period: days });
            }

            const remainingTasks = tasks.filter((task) => !selectedTasks.includes(task.id));
            setTasks(remainingTasks);
            setSelectedTasks([]);
            setSelectAll(false);

            toast.success(`${selectedTasks.length} task(s) moved by ${days} day${days > 1 ? "s" : ""} successfully`);

            fetchStaticTaskList();
        } catch (error) {
            toast.error("Failed to move tasks");
        }
    };

    const handleDateChange = async (date) => {
        if (date && selectedTasks.length > 0) {
            try {
                const selectedDateMs = new Date(date).getTime();

                await updateTasksTime(selectedTasks, { zaman: selectedDateMs });

                const remainingTasks = tasks.filter((task) => !selectedTasks.includes(task.id));
                setTasks(remainingTasks);
                setSelectedTasks([]);
                setSelectAll(false);
                setShowDatePicker(false);

                toast.success(
                    `${selectedTasks.length} task(s) moved to ${moment(date).format("MMM DD, YYYY")} successfully`
                );

                fetchStaticTaskList();
            } catch (error) {
                toast.error("Failed to move tasks");
            }
        }
    };

    const handleFilterToggle = () => {
        setShowFilter(!showFilter);
    };

    const filteredTasks = useMemo(() => {
        if (selectedTaskTypes.length === 0) {
            return tasks;
        }

        const selectedTypes = selectedTaskTypes.map((option) => option.value);

        const filtered = tasks.filter((task) => {
            return task.type && selectedTypes.includes(task.type);
        });

        return filtered;
    }, [tasks, selectedTaskTypes]);

    return (
        <>
            <TaskListContainer>
                <DateHeaderContainer>
                    {showOverdueTasks ? (
                        <ExtensionCardHeader
                            cardTitle={<HeaderTitle>All Overdues</HeaderTitle>}
                            button={
                                <ButtonWithText
                                    onClick={() => {
                                        setState({
                                            popupContent: POPUP_CONTENTS.NEW_TASK,
                                            popupIsOpen: true,
                                            taskPage: true,
                                            editMode: false,
                                        });
                                    }}
                                    buttonStyle="default"
                                    icon="add"
                                    iconType="material"
                                    variant="l3rnTech"
                                    type="default"
                                    size="circle"
                                />
                            }
                        />
                    ) : (
                        <DateHeader highlightedDates={!taskListLoading ? highlightedDates : undefined} />
                    )}
                </DateHeaderContainer>
                {!taskListLoading && tasks.length > 0 && (
                    <>
                        <SelectAllContainer>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <SelectAllCheckbox
                                    checked={selectAll}
                                    onClick={handleSelectAll}
                                    role="checkbox"
                                    aria-checked={selectAll}
                                />
                                <SelectAllLabel onClick={handleSelectAll}>Select All Tasks</SelectAllLabel>
                            </div>
                            <ButtonWithText
                                size="xxsmall"
                                buttonStyle={showFilter ? "default" : "inverted"}
                                variant={showFilter ? "l3rnTech" : "passive"}
                                onClick={handleFilterToggle}
                            >
                                <SVG
                                    src={toAbsoluteUrl("/media/svg/icons/Text/Filters2.svg")}
                                    style={{ width: "12px", height: "12px" }}
                                />
                            </ButtonWithText>
                        </SelectAllContainer>
                    </>
                )}
                {showFilter && (
                    <FilterBar>
                        <TaskTypeFilterWrapper>
                            <ExtensionInputGroup
                                variant="l3rnTech"
                                label={"Task Type"}
                                type="select"
                                placeholder="Please Select Task Type"
                                options={tagOptions}
                                isMulti={true}
                                onChange={(selected) => {
                                    setSelectedTaskTypes(selected || []);
                                }}
                                value={selectedTaskTypes}
                                hideLabel={true}
                            />
                        </TaskTypeFilterWrapper>
                    </FilterBar>
                )}
                <TasksContainer ref={tasksContainerRef}>
                    {taskListLoading || (showOverdueTasks && isOverdueTasksLoading && tasks.length === 0) ? (
                        <LoadingContainer>
                            <Spinner animation="border" variant="l3rnTech" />
                        </LoadingContainer>
                    ) : !tasks.length ? (
                        <NoTasksMessage>No tasks found</NoTasksMessage>
                    ) : selectedTaskTypes.length > 0 && !filteredTasks.length ? (
                        <NoTasksMessage>No tasks match the selected task types</NoTasksMessage>
                    ) : (
                        <div>
                            {(() => {
                                let globalIndex = 0;
                                return Object.entries(groupTasksByTime(filteredTasks)).map(([time, groupTasks]) => (
                                    <TimeGroup key={time}>
                                        <DashedDivider />
                                        <TimeDisplay>{time}</TimeDisplay>
                                        {groupTasks.map((task, idx) => {
                                            const currentGlobalIndex = globalIndex;
                                            globalIndex++;
                                            return (
                                                <StyledTaskContainer
                                                    key={task.id}
                                                    ref={(el) => {
                                                        if (el) {
                                                            taskRefs.current[task.id] = el;
                                                        }
                                                    }}
                                                >
                                                    <SelectableTaskCard
                                                        task={task}
                                                        index={currentGlobalIndex}
                                                        setTaskToDelete={setTaskToDelete}
                                                        selectedTasks={selectedTasks}
                                                        onTaskComplete={handleTaskCompletion}
                                                        overdue={
                                                            showOverdueTasks ||
                                                            (!showOverdueTasks && moment(task.zaman).isBefore(moment()))
                                                        }
                                                        greyMode={selectedTasks.length > 0}
                                                        hideBulkActions={selectedTasks.length > 1 || selectAll}
                                                        isCompletedTask={isActive}
                                                        isFirst={true}
                                                        isInOverduesPage={showOverdueTasks}
                                                    />
                                                </StyledTaskContainer>
                                            );
                                        })}
                                    </TimeGroup>
                                ));
                            })()}
                            <div ref={loaderRef} style={{ display: "flex", justifyContent: "center" }}>
                                {showOverdueTasks && hasMoreOld && <Spinner animation="border" variant="primary" />}
                            </div>
                        </div>
                    )}
                </TasksContainer>
            </TaskListContainer>

            {(selectedTasks.length > 1 || selectAll) && (
                <BulkActionsFooter>
                    <BulkActionsContainer>
                        {!isActive && (
                            <ButtonWithText
                                size="small"
                                variant="success"
                                buttonStyle="borderless"
                                onClick={handleComplete}
                                disabled={selectedTasks.length === 0 || completeLoading}
                            >
                                {completeLoading ? "Completing..." : "Complete"}
                            </ButtonWithText>
                        )}
                        <MoveButtonWrapper ref={moveButtonRef} showDatePicker={showDatePicker}>
                            <ButtonWithText
                                size="small"
                                variant="passive"
                                buttonStyle="borderless"
                                disabled={selectedTasks.length === 0 || moveLoading}
                            >
                                {moveLoading ? "Moving..." : "Move"}
                            </ButtonWithText>
                            <MoveDropdown ref={moveDropdownRef} className="move-dropdown">
                                <MoveOption onClick={() => handleMove(1)}>+1 day</MoveOption>
                                <MoveOption onClick={() => handleMove(3)}>+3 days</MoveOption>
                                <MoveOption onClick={() => handleMove(7)}>+7 days</MoveOption>
                                <MoveOption onClick={() => handleMove("other")}>Other</MoveOption>
                            </MoveDropdown>
                            {showDatePicker && (
                                <DatePickerWrapper ref={datePickerRef}>
                                    <CustomDatePicker
                                        variant="move-dropdown"
                                        componentVariant={"l3rnTech"}
                                        onChange={handleDateChange}
                                        onClickOutside={() => setShowDatePicker(false)}
                                        showMonthYearDropdown={true}
                                    />
                                </DatePickerWrapper>
                            )}
                        </MoveButtonWrapper>
                        <ButtonWithText
                            size="small"
                            variant="danger"
                            buttonStyle="borderless"
                            onClick={handleDelete}
                            disabled={selectedTasks.length === 0 || deleteLoading}
                        >
                            {deleteLoading ? "Deleting..." : "Delete"}
                        </ButtonWithText>
                    </BulkActionsContainer>
                </BulkActionsFooter>
            )}

            <StyledButtonContainer>
                {showOverdueTasks ? (
                    <ButtonWithText
                        size="xsmall"
                        variant="l3rnTech"
                        buttonStyle="borderless"
                        onClick={handleShowTodayTasks}
                    >
                        Today
                    </ButtonWithText>
                ) : (
                    <>
                        <ButtonWithText
                            size="xsmall"
                            variant="danger"
                            buttonStyle="borderless"
                            onClick={handleShowOverdueTasks}
                            style={{ padding: "0px" }}
                            disabled={isOverdueTasksLoading}
                        >
                            {isOverdueTasksLoading ? (
                                <>
                                    <Spinner
                                        animation="border"
                                        size="sm"
                                        style={{ width: "12px", height: "12px", marginRight: "4px" }}
                                    />
                                    Loading...
                                </>
                            ) : (
                                "Show All Overdues"
                            )}
                        </ButtonWithText>
                        <StyledFlex>
                            <StyledText>Only Completed</StyledText>
                            <Switch
                                variant="l3rnTech"
                                isActive={isActive}
                                onClick={() => {
                                    const newValue = !isActive;
                                    setIsActive(newValue);
                                    if (newValue) {
                                        setState((prevState) => ({
                                            ...prevState,
                                            showOverdueTasks: false,
                                        }));
                                    }
                                }}
                            />
                        </StyledFlex>
                    </>
                )}
            </StyledButtonContainer>
            <Modal
                isOpen={selectedModal === modals.DELETE_BULK_TASKS}
                successButtonText="Yes, Delete"
                cancelButtonText="Not Now"
                onClose={() =>
                    setState((prevState) => ({
                        ...prevState,
                        selectedModal: null,
                    }))
                }
                title={modalMessage || "Are you sure want to delete all selected task?"}
                variant="danger"
                icon="delete"
                onSuccess={() => {
                    handleBulkDelete();
                    setState((prevState) => ({
                        ...prevState,
                        selectedModal: null,
                    }));
                }}
            >
                <div>This action cannot be undone.</div>
            </Modal>
        </>
    );
};

export default ExtensionTaskList;
